import { queryOptions, type QueryClient } from "@tanstack/react-query"
import { api } from "./api"
import { listAllData } from "./list-all"
import type { RichTextDoc } from "./rich-text"
import type { FileAttachment } from "./attachments"
import type { MessageReactionTally } from "./message-view"

// Support-ticket data layer for the Inbox slice. Extracted from apps/admin's
// inline InboxPage queries into reusable query-option factories. CRITICAL: the
// query keys here match admin byte-for-byte (`["resource", <table>, <filters>]`)
// so the vendored realtime layer (realtime.ts → cache.ts) surgically patches
// these caches on live CRUD events with no extra wiring.

export type SupportTicket = {
  id: string
  eventId: string
  // The guest side: account id (null for a guest with no account) and/or the
  // guest id (pass-opened tickets, and team-sent tickets to guests).
  guestUserId: string | null
  openerGuestId?: string | null
  subject: string
  status: string
  closedAt: string | null
  closedById: string | null
  assigneeUserId: string | null
  priority: string | null
  guestLastReadAt: string | null
  // Shared "any staff read" — feeds the guest's ✓✓ only; NOT anyone's unread.
  teamLastReadAt: string | null
  createdAt: string
  modifiedAt: string | null
  // Per-user read state, merged from `my-ticket-reads` (THE unread rule —
  // comments newer than MY pointer, not by me). Absent/0 = read.
  unread?: number
  myLastReadAt?: string | null
  myLastReadMessageId?: string | null
}

export type TicketRead = { unread: number; lastReadAt: string | null; lastReadMessageId: string | null }

export type TicketComment = {
  id: string
  ticketId: string
  // NULL when a sessionless attendee authored the comment (guest path) — the
  // server sets `authorGuestId` instead. `authorNameAtSend` is always set.
  authorUserId: string | null
  authorGuestId?: string | null
  authorNameAtSend: string
  authorRoleAtSend: string | null
  body: string
  // Constrained Tiptap JSON projection of `body` (string-encoded). NULL =
  // render `body` as plain text. Authored by the rich composer; legacy
  // surfaces (admin/iOS/guest) fall back to `body`.
  richText: string | null
  kind: string
  visibility: string | null
  createdAt: string
  editedAt: string | null
  // Soft-delete (moderation): set by deleteTicketComment when an admin/owner or
  // the author removes the comment. The read endpoint returns the redacted row
  // so the unified row can render a "message removed" tombstone. Null = live.
  deletedAt?: string | null
  // JSON array of attachments (file/image), stored verbatim + returned on read.
  // Null when the comment has none. Parsed by parseMessageAttachments.
  attachments?: string | null
  // JSON array of link unfurl cards; written async (waitUntil) post-send and
  // re-broadcast as an `edited` frame. Raw JSON, rendered by LinkUnfurl. Null = none.
  unfurls?: string | null
  // Client-only optimistic placeholder; cleared when the server echo reconciles.
  pending?: boolean
  clientId?: string | null
}

export type Guest = {
  id: string
  linkedUserId: string | null
  nameAtInvite: string | null
  emailAtInvite: string | null
  displayName: string | null
}

export type MemberRow = {
  userId: string
  role?: string
  user?: { id: string; name: string | null; email: string | null }
}

type ListEnvelope<T> = {
  data: T[]
  meta?: { total?: number }
  pagination?: { hasMore?: boolean; count?: number; page?: number; pageSize?: number }
}
type TicketThreadEnvelope = {
  success: true
  data: TicketComment[]
  nextCursor: string | null
  hasMore: boolean
  teamLastReadAt: string | null
  guestLastReadAt: string | null
  // Grouped reactions (parentType "ticket") keyed by comment id, bundled by
  // list-ticket-thread so the panel seeds useReactions without a second fetch.
  reactions?: Record<string, MessageReactionTally[]>
}

export type InboxBundle = {
  success: true
  tickets: SupportTicket[]
  guests: Guest[]
  members: MemberRow[]
  invitations: unknown[]
  initialTicketId: string | null
  comments: TicketComment[]
}

// ── query keys (must match apps/admin) ──────────────────────────────────────
export const ticketsKey = (eventId: string) =>
  ["resource", "support-tickets", { eventId }] as const
export const ticketCommentsKey = (ticketId: string) =>
  ["resource", "ticket-comments", { ticketId }] as const
export const guestsLookupKey = (eventId: string) =>
  ["resource", "guests", { eventId, mode: "inbox-lookup" }] as const

// ── query options ───────────────────────────────────────────────────────────
// Ticket rows (raw CRUD list — the realtime layer patches THIS key) merged with
// my per-user read state in the same fetch, so every consumer reads `t.unread`
// off one cache. The `chat.inbox.changed` nudge invalidates the key on every
// ticket mutation, which refetches both halves.
export function ticketsQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: ticketsKey(eventId),
    queryFn: async () => {
      const [list, reads] = await Promise.all([
        listAllData<SupportTicket>("/api/v1/support-tickets", {
          eventId,
          sort: "modifiedAt",
          order: "desc",
        }),
        api<{ reads: Record<string, TicketRead> }>(
          `/api/v1/event/${eventId}/chat/my-ticket-reads`,
          { method: "POST", body: {} },
        ).catch(() => ({ reads: {} as Record<string, TicketRead> })),
      ])
      return {
        ...list,
        data: list.data.map((t) => {
          const r = reads.reads[t.id]
          return r
            ? { ...t, unread: r.unread, myLastReadAt: r.lastReadAt, myLastReadMessageId: r.lastReadMessageId }
            : t
        }),
      }
    },
    select: (r) => r.data,
  })
}

export function ticketCommentsQueryOptions(eventId: string, ticketId: string) {
  return queryOptions({
    queryKey: ticketCommentsKey(ticketId),
    // list-ticket-thread returns newest-first (DESC). Reverse to ASC HERE so the
    // STORED cache is oldest→newest — the order every writer already assumes: the
    // optimistic append in ConversationPanel, the realtime "append" splice
    // (realtime.ts tryMessageInsertSplice), loadOlder's older-block prepend, and
    // the bundle seed. Reversing in `select` (read-time) instead left the cache
    // DESC, so an appended optimistic comment landed at the OLDEST slot and
    // rendered at the TOP, then "dropped" to the bottom on the next realtime
    // refetch. Reverse once, at storage time, and all paths agree.
    queryFn: async () => {
      const r = await api<TicketThreadEnvelope>(
        `/api/v1/event/${eventId}/chat/list-ticket-thread`,
        { method: "POST", body: { ticketId, limit: 100 } },
      )
      return { ...r, data: r.data.slice().reverse() }
    },
  })
}

export function fetchTicketCommentsPage(
  eventId: string,
  ticketId: string,
  before: string | null,
  limit = 100,
) {
  return api<TicketThreadEnvelope>(
    `/api/v1/event/${eventId}/chat/list-ticket-thread`,
    {
      method: "POST",
      body: {
        ticketId,
        limit,
        ...(before ? { before } : {}),
      },
    },
  )
}

export function guestsLookupQueryOptions(eventId: string) {
  return queryOptions({
    queryKey: guestsLookupKey(eventId),
    queryFn: () =>
      listAllData<Guest>("/api/v1/guests", { eventId }),
    select: (r) => r.data,
  })
}

// One round trip that seeds tickets + guests + the initial thread's comments
// into the SAME cache keys the lists read — so realtime keeps patching them.
export async function loadInboxBundle(
  qc: QueryClient,
  eventId: string,
  deepLinkTicketId?: string | null,
): Promise<InboxBundle> {
  const b = await api<InboxBundle>(`/api/v1/event/${eventId}/chat/get-inbox-admin`, {
    method: "POST",
    body: { ticketId: deepLinkTicketId ?? undefined },
  })
  qc.setQueryData<ListEnvelope<SupportTicket>>(ticketsKey(eventId), { data: b.tickets })
  qc.setQueryData<ListEnvelope<Guest>>(guestsLookupKey(eventId), { data: b.guests })
  if (b.initialTicketId && b.comments) {
    qc.setQueryData<TicketThreadEnvelope>(ticketCommentsKey(b.initialTicketId), {
      success: true,
      // get-inbox-admin returns these comments ASC (oldest→newest) already —
      // the same orientation the cache now stores (see ticketCommentsQueryOptions),
      // so seed them as-is. (Was `.reverse()` back when the cache was DESC.)
      data: b.comments.slice(),
      nextCursor: null,
      hasMore: false,
      teamLastReadAt: null,
      guestLastReadAt: null,
    })
  }
  return b
}

// ── mutation ─────────────────────────────────────────────────────────────────
export type PostCommentInput = {
  ticketId: string
  body: string
  // Public (guest + team) vs internal (team-only note). NOTE: the backend
  // action's input field is `visibility` — `kind` is a DB column ("text" /
  // "system"), NOT an input. We carry it as `kind` client-side (it drives the
  // optimistic bubble) but send it to the server as `visibility`.
  kind: "public" | "internal"
  clientId: string
  // Rich Tiptap doc; the server sanitizes + stores it as richText (body is the
  // flattened plain-text fallback). Optional — omit for plain text.
  richTextDoc?: RichTextDoc
  // Uploaded file attachments to stash on the comment (presigned-R2 mediaIds).
  attachments?: FileAttachment[]
}

export async function postTicketComment(
  eventId: string,
  input: PostCommentInput,
): Promise<{ comment: TicketComment }> {
  return api<{ comment: TicketComment }>(
    `/api/v1/event/${eventId}/chat/post-ticket-comment`,
    {
      method: "POST",
      body: {
        ticketId: input.ticketId,
        body: input.body,
        // Map client `kind` → server `visibility` (the action's actual field).
        visibility: input.kind,
        clientId: input.clientId,
        ...(input.richTextDoc ? { richTextDoc: input.richTextDoc } : {}),
        ...(input.attachments && input.attachments.length > 0
          ? { attachments: input.attachments }
          : {}),
      },
    },
  )
}

// ── display helpers ──────────────────────────────────────────────────────────
export function guestNameMap(guests: Guest[]): Map<string, string> {
  const m = new Map<string, string>()
  for (const g of guests) {
    const name = g.displayName ?? g.nameAtInvite ?? g.emailAtInvite ?? null
    if (!name) continue
    // Keyed by account id AND guest id — a ticket's guest side may be either.
    if (g.linkedUserId) m.set(g.linkedUserId, name)
    m.set(g.id, name)
  }
  return m
}

/** Display name of a ticket's guest side (account id, else guest id). */
export function ticketGuestName(
  t: Pick<SupportTicket, "guestUserId" | "openerGuestId">,
  names: Map<string, string>,
): string {
  return (t.guestUserId && names.get(t.guestUserId)) || (t.openerGuestId && names.get(t.openerGuestId)) || "Guest"
}

export function memberNameMap(members: MemberRow[]): Map<string, string> {
  const m = new Map<string, string>()
  for (const mem of members) {
    const id = mem.user?.id ?? mem.userId
    const name = mem.user?.name ?? mem.user?.email ?? null
    if (id && name) m.set(id, name)
  }
  return m
}

export type Priority = "urgent" | "high" | "normal" | "low"

export function priorityRank(p: string | null): number {
  switch (p) {
    case "urgent":
      return 0
    case "high":
      return 1
    case "low":
      return 3
    default:
      return 2
  }
}

export function isTicketOpen(t: SupportTicket): boolean {
  return t.status !== "closed" && t.closedAt == null
}

// Unread = the server-computed per-user count (THE unread rule). Never derived
// from modifiedAt (triage metadata is not activity) or teamLastReadAt (shared).
export function isTicketUnread(t: SupportTicket): boolean {
  return (t.unread ?? 0) > 0
}

// Front/Zendesk scope: a ticket "needs me" when it's open and either assigned
// to me or unassigned. Tickets owned by a teammate still show their row dot but
// never badge the rail / @Me / dock.
export function ticketNeedsMe(t: SupportTicket, meId: string | null): boolean {
  if (!isTicketOpen(t)) return false
  return t.assigneeUserId == null || (!!meId && t.assigneeUserId === meId)
}

// ── triage vocab (matches the backend status/priority enums) ────────────────
export type TicketStatus = "open" | "pending" | "on_hold" | "closed"
export const TICKET_STATUSES: TicketStatus[] = ["open", "pending", "on_hold", "closed"]
export const TICKET_PRIORITIES: Priority[] = ["urgent", "high", "normal", "low"]

export function statusLabel(s: string): string {
  switch (s) {
    case "open":
      return "Open"
    case "pending":
      return "Pending"
    case "on_hold":
      return "On hold"
    case "closed":
      return "Closed"
    default:
      return s ? s.replace(/[_-]/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Open"
  }
}

export function priorityLabel(p: string | null | undefined): string {
  const v = p ?? "normal"
  return v.replace(/^./, (c) => c.toUpperCase())
}

// ── triage mutations (mirror apps/admin/InboxPage) ──────────────────────────
// Every action returns the updated ticket row; we splice it into the list
// cache (the same key the realtime layer patches) instead of refetching.
type TicketActionResult = {
  success: true
  ticket?: SupportTicket
  unchanged?: boolean
  alreadyClosed?: boolean
  alreadyOpen?: boolean
}

export async function assignSupportTicket(
  eventId: string,
  ticketId: string,
  assigneeUserId: string | null,
): Promise<TicketActionResult> {
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/assign-support-ticket`, {
    method: "POST",
    body: { ticketId, assigneeUserId },
  })
}

export async function setSupportTicketPriority(
  eventId: string,
  ticketId: string,
  priority: Priority,
): Promise<TicketActionResult> {
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/set-support-ticket-priority`, {
    method: "POST",
    body: { ticketId, priority },
  })
}

export async function setSupportTicketStatus(
  eventId: string,
  ticketId: string,
  status: TicketStatus,
): Promise<TicketActionResult> {
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/set-support-ticket-status`, {
    method: "POST",
    body: { ticketId, status },
  })
}

export async function closeSupportTicket(
  eventId: string,
  ticketId: string,
): Promise<TicketActionResult> {
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/close-support-ticket`, {
    method: "POST",
    body: { ticketId },
  })
}

export async function reopenSupportTicket(
  eventId: string,
  ticketId: string,
): Promise<TicketActionResult> {
  return api<TicketActionResult>(`/api/v1/event/${eventId}/chat/reopen-support-ticket`, {
    method: "POST",
    body: { ticketId },
  })
}

export async function markTicketRead(
  eventId: string,
  ticketId: string,
  lastReadMessageId?: string | null,
): Promise<{ success: true; side: "team" | "attendee"; lastReadAt: string; lastReadMessageId?: string | null }> {
  return api(`/api/v1/event/${eventId}/chat/mark-ticket-read`, {
    method: "POST",
    body: { ticketId, ...(lastReadMessageId ? { lastReadMessageId } : {}) },
  })
}

// Open a NEW ticket to one or many guests via the group-concierge action.
// One ticket, N participants (single recipient → a 1:1 ticket). Supports
// dryRun for the review step.
export type NewTicketResult = {
  success: true
  committed: boolean
  recipientCount: number
  recipientUserIds?: string[]
  recipientGuestIds?: string[]
  ticketId?: string
  participantUserIds?: string[]
  participantGuestIds?: string[]
  // Accounts the rule resolved that have no guest row in this event — never
  // messaged, so the caller must say so (always empty for a guest_ids rule).
  skippedUserIds?: string[]
  tickets?: Array<{ ticketId: string; guestUserId: string }>
}

// Keyed on GUEST ids (not accounts) so Event-Pass-only guests with no
// `linkedUserId` are reachable too.
export async function sendNewTicket(input: {
  eventId: string
  guestIds: string[]
  subject: string
  body: string
  dryRun: boolean
}): Promise<NewTicketResult> {
  return api<NewTicketResult>("/api/v1/chat/send-to-support-tickets", {
    method: "POST",
    body: {
      eventId: input.eventId,
      subject: input.subject,
      body: input.body,
      recipientRule: { kind: "guest_ids", guestIds: input.guestIds },
      dryRun: input.dryRun,
    },
  })
}

// Splice an updated ticket row into the list cache (the realtime-patched key).
// Returns true when the row was found + patched; false → caller should fall
// back to invalidating the list.
export function patchTicketInCache(
  qc: QueryClient,
  eventId: string,
  ticket: Partial<SupportTicket> & { id: string },
): boolean {
  let patched = false
  qc.setQueryData<ListEnvelope<SupportTicket>>(ticketsKey(eventId), (old) => {
    if (!old) return old
    const next = old.data.map((t) => {
      if (t.id !== ticket.id) return t
      patched = true
      return { ...t, ...ticket }
    })
    return patched ? { ...old, data: next } : old
  })
  return patched
}
